import { Box, styled, Typography } from "@material-ui/core";
import {
  EPOCUS_BORDER,
  EPOCUS_TEXT_PRIMARY,
  EPOCUS_TEXT_SECONDARY,
  EPOCUS_WHITE,
  rem,
} from "../../_lib/epocus-palette";
import { epocusTheme } from "../../_lib/epocus-theme-provider";
import type { EpocusBillingDayQuery } from "./__generated__/billing-operations.gql";
import {
  formatEstimateCents,
  MEDICARE_ESTIMATE_LABEL,
  NEEDS_PROGRAM_POSTAL_CODE_LABEL,
} from "./service-day";

const KpiRow = styled(Box)({
  display: "grid",
  gridTemplateColumns: "1fr",
  gap: 12,
  [epocusTheme.breakpoints.up("sm")]: {
    gridTemplateColumns: "repeat(2, 1fr)",
  },
  [epocusTheme.breakpoints.up("md")]: {
    gridTemplateColumns: "repeat(4, 1fr)",
  },
});

const Kpi = styled(Box)({
  backgroundColor: EPOCUS_WHITE,
  border: `1px solid ${EPOCUS_BORDER}`,
  borderRadius: 12,
  padding: 14,
});

export const BillingKpis: FC<{
  billableCount: number;
  heldBackCount: number;
  showFees: boolean;
  feeTotals: EpocusBillingDayQuery["epocusBillingDay"]["feeTotals"] | undefined;
}> = ({ billableCount, heldBackCount, showFees, feeTotals }) => {
  const needsPostalCode = feeTotals?.needsProgramPostalCode ?? false;
  const fee = (cents: number | null | undefined) =>
    !needsPostalCode && cents != null ? formatEstimateCents(cents) : "$—";
  const feeNote = (cents: number | null | undefined) => {
    if (needsPostalCode) return NEEDS_PROGRAM_POSTAL_CODE_LABEL;
    if (cents == null && (feeTotals?.unestimatedLineCount ?? 0) > 0) {
      return "No estimate for these codes";
    }
    return MEDICARE_ESTIMATE_LABEL;
  };

  return (
    <KpiRow>
      <BillingKpi
        label="Ready to bill"
        value={String(billableCount)}
        note="signed, nothing missing"
      />
      <BillingKpi
        label="Incomplete"
        value={String(heldBackCount)}
        note="not signed, or missing an item"
      />
      {showFees && (
        <>
          <BillingKpi
            label="Est. fees, ready to bill"
            value={fee(feeTotals?.readyToBillCents)}
            note={feeNote(feeTotals?.readyToBillCents)}
          />
          <BillingKpi
            label="Est. fees, incomplete"
            value={fee(feeTotals?.incompleteCents)}
            note={feeNote(feeTotals?.incompleteCents)}
          />
        </>
      )}
    </KpiRow>
  );
};

const BillingKpi: FC<{ label: string; value: string; note: string }> = ({
  label,
  value,
  note,
}) => (
  <Kpi>
    <Typography
      style={{
        fontSize: rem(11),
        fontWeight: 700,
        letterSpacing: "0.07em",
        textTransform: "uppercase",
        color: EPOCUS_TEXT_SECONDARY,
      }}
    >
      {label}
    </Typography>
    <Typography
      style={{
        fontSize: rem(28),
        fontWeight: 700,
        color: EPOCUS_TEXT_PRIMARY,
        lineHeight: 1.2,
      }}
    >
      {value}
    </Typography>
    <Typography style={{ fontSize: rem(12), color: EPOCUS_TEXT_SECONDARY }}>
      {note}
    </Typography>
  </Kpi>
);
